import { IAlternativeUnits, IGetAllProductsResult } from '../actions/create-order/get-all-products.action';
import { IPostCartItems } from '../actions/create-order/post-cart.action';
import { clampQuantityValue, formatSliceNumber } from './format-number';

/**
 *
 * @param product the product to update
 * @param unitOfMeasure the unit to convert into
 * @param convertBackwards convert already converted value?
 * @returns
 */
export const getConversionFactor = (product: IGetAllProductsResult, unitOfMeasure: string, convertBackwards: boolean): number => {
    let converted: number;

    if (unitOfMeasure !== product.BaseUnit) {
        const alternativeUnit = product?.AlternativeUnits?.find((unit: IAlternativeUnits) => unit.Unit === unitOfMeasure);
        if (alternativeUnit) {
            converted = convertBackwards
                ? Number(alternativeUnit.Denomerator) / Number(alternativeUnit.Numerator)
                : Number(alternativeUnit.Numerator) / Number(alternativeUnit.Denomerator);
            return converted;
        }
    }
    return 1;
};

/**
 * Convert quantity between two units for the same product via base-unit normalization.
 * Precision is stabilized so repeated round-trips remain numerically stable.
 */
export const convertQuantityBetweenUnits = (product: IGetAllProductsResult, quantity: number, fromUnit: string, toUnit: string): number => {
    if (fromUnit === toUnit) {
        return quantity;
    }

    const fromToBase = getConversionFactor(product, fromUnit, false);
    const toToBase = getConversionFactor(product, toUnit, false);

    if (!Number.isFinite(fromToBase) || !Number.isFinite(toToBase) || toToBase === 0) {
        return quantity;
    }

    const quantityInBase = quantity * fromToBase;
    const converted = quantityInBase / toToBase;
    const precise = Number(converted.toPrecision(14));
    // If the result is very close to an integer, round it to avoid floating point issues in downstream comparisons
    return Math.abs(precise - Math.round(precise)) < 1e-9 ? Math.round(precise) : precise;
};

/**
 * Convert a product quantity from one unit to another, returning a string representation of the converted quantity.
 * @param product the product to use for conversion (contains base/sales unit and alternative unit info)
 * @param quantity the quantity to convert
 * @param fromUnit the unit of the supplied quantity
 * @param toUnit the unit to convert into
 * @returns the converted quantity as a string, or the original quantity as a string if conversion is not possible or necessary
 */
export const convertProductQuantityToUnit = (
    product: IGetAllProductsResult | undefined,
    quantity: number,
    fromUnit: string | undefined,
    toUnit: string | undefined
): string => {
    if (!product || !fromUnit || !toUnit || fromUnit === toUnit) {
        return quantity.toString();
    }

    const convertedQuantity = convertQuantityBetweenUnits(product, quantity, fromUnit, toUnit);

    return Number(convertedQuantity.toPrecision(12)).toString();
};

/**
 * Get the packaging quantity for a product in a specified target unit, converting from the product's packaging unit if necessary.
 * @param product the product to get the packaging quantity for (contains PackagingQty, PackagingQtyUnit, BaseUnit, UnitOfMeasure)
 * @param targetUnit the unit to express the packaging quantity in (e.g. the cart line unit), if conversion is needed
 * @param fallbackUnit an optional unit to use if targetUnit is not provided, otherwise the product's UnitOfMeasure will be used as the default target unit for conversion
 * @returns the packaging quantity expressed in the target unit as a string, or the original packaging quantity as a string if conversion is not possible or necessary
 */
export const getPackagingQuantityForUnit = (
    product: IGetAllProductsResult | undefined,
    targetUnit: string | undefined,
    fallbackUnit?: string | undefined
): string => {
    const rawPackagingQty = product?.PackagingQty ? Number(product.PackagingQty.replace(/,/g, '.')) : 0;
    const packagingUnit = product?.PackagingQtyUnit || product?.BaseUnit;
    const normalizedTargetUnit = targetUnit || fallbackUnit || product?.UnitOfMeasure;

    if (!product || !rawPackagingQty || !packagingUnit || !normalizedTargetUnit || packagingUnit === normalizedTargetUnit) {
        return rawPackagingQty.toString();
    }

    return convertProductQuantityToUnit(product, rawPackagingQty, packagingUnit, normalizedTargetUnit);
};

/**
 * Get a quantity representing the number of whole packages needed to contain a given quantity of product, expressed in a specified unit.
 * @param quantity the quantity to fit into packages, expressed in the specified unit
 * @param unitOfMeasure the unit of measure for the input quantity (e.g. the cart line unit)
 * @param product the product to use for conversion and packaging info (contains PackagingQty, PackagingQtyUnit, BaseUnit, UnitOfMeasure)
 * @returns the quantity of whole packages needed to contain the input quantity, expressed in the specified unit, as a string; or undefined if conversion is not possible
 */
export const getRoundedPackagingQuantityInUnit = (
    quantity: number,
    unitOfMeasure: string,
    product: IGetAllProductsResult | undefined
): string | undefined => {
    const rawPackagingQty = product?.PackagingQty ? Number(product.PackagingQty.replace(/,/g, '.')) : 0;
    const packagingUnit = product?.PackagingQtyUnit || product?.BaseUnit;

    if (!product || !rawPackagingQty || !packagingUnit) {
        return undefined;
    }

    const quantityInPackagingUnit = convertQuantityBetweenUnits(product, quantity, unitOfMeasure, packagingUnit);
    const normalizedInputQuantity = Number(clampQuantityValue(quantity, unitOfMeasure, product));
    const getNormalizedPackagedQuantity = (packageCount: number): string => {
        const packagedQuantity = packageCount * rawPackagingQty;
        const convertedPackagedQuantity = convertQuantityBetweenUnits(product, packagedQuantity, packagingUnit, unitOfMeasure);

        return clampQuantityValue(convertedPackagedQuantity, unitOfMeasure, product);
    };

    const packageCount = Math.max(1, Math.ceil(quantityInPackagingUnit / rawPackagingQty));
    const normalizedPackagedQuantity = getNormalizedPackagedQuantity(packageCount);

    if (Number(normalizedPackagedQuantity) >= normalizedInputQuantity) {
        return normalizedPackagedQuantity;
    }

    return getNormalizedPackagedQuantity(packageCount + 1);
};

/**
 * Convert a quantity from a given unit into the product's base unit.
 *
 * @param product the product to use for conversion (contains base/sales unit info)
 * @param fromUnit the unit of the supplied quantity
 * @param quantity the quantity to convert (string or number)
 * @returns an object exposing getter functions for the converted quantity and unit,
 *          plus direct properties for convenience
 */
export const convertQuantity = (
    product: IGetAllProductsResult,
    fromUnit: string,
    quantity: string | number
): {
    getConvQty: () => number;
    getConvUnit: () => string;
    qty: number;
    unit: string;
} => {
    const unit = product?.SalesUnit || product?.BaseUnit;

    // Normalize numeric input
    const parsedQty = typeof quantity === 'string' ? Number(quantity.trim()) : Number(quantity);
    if (!Number.isFinite(parsedQty)) {
        return {
            getConvQty: () => 0,
            getConvUnit: () => unit,
            qty: 0,
            unit
        };
    }

    const converted = convertQuantityBetweenUnits(product, parsedQty, fromUnit, unit);
    const normalizedQuantity = clampQuantityValue(converted, unit, product);
    const rounded = Number(normalizedQuantity || '0');

    return {
        getConvQty: () => rounded,
        getConvUnit: () => unit,
        qty: rounded,
        unit
    };
};

/**
 * Determine the purchase quantity and unit to use for a cart item,
 * converting when the cart unit differs from the product's base/sales unit.
 *
 * @param productsData the product master data list to find matching product info
 * @param product the cart item (contains ProductNumber, UnitOfMeasure, Quantity)
 * @returns an object exposing getter functions for the resolved quantity and unit
 */
export const getPurchaseQuantity = (
    productsData: IGetAllProductsResult[],
    product: IPostCartItems
): {
    getQuantity: () => string;
    getUnit: () => string;
} => {
    const match = productsData.find(p => p.ProductNumber === product.ProductNumber);
    const targetUnit = match?.SalesUnit || match?.BaseUnit;

    const getQuantity = (): string => {
        // If we have a match and the units differ, convert into the product's base/sales unit
        if (match && targetUnit && product.UnitOfMeasure !== targetUnit) {
            const convertedQuantity = convertQuantity(match, product.UnitOfMeasure, product.Quantity).getConvQty();
            return formatSliceNumber(convertedQuantity.toString());
        }
        // otherwise return original quantity
        return product.Quantity;
    };

    const getUnit = (): string => {
        if (match && targetUnit && product.UnitOfMeasure !== targetUnit) {
            return targetUnit;
        }
        return product.UnitOfMeasure;
    };

    return { getQuantity, getUnit };
};
